'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex touch-manipulation select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium ring-offset-navy-950 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/70 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-r from-gold-600 via-gold-500 to-gold-400 text-navy-950 shadow-gold-glow hover:shadow-gold-glow-lg btn-shimmer',
        secondary:
          'border border-gold-500/40 bg-gold-500/10 text-gold-300 hover:bg-gold-500/15 hover:border-gold-500/60',
        ghost: 'text-cream/80 hover:bg-white/5 hover:text-cream',
        outline:
          'border border-white/15 bg-transparent text-cream hover:bg-white/5 hover:border-white/30',
        link: 'text-gold-300 underline-offset-4 hover:underline rounded-none',
        destructive:
          'bg-danger/90 text-white hover:bg-danger',
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-sm',
        lg: 'h-13 px-8 text-base [height:3.25rem]',
        xl: 'h-14 px-10 text-base font-semibold',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
